import { balance as defaultBalance, type Balance } from '../balance';
import { lineupOf } from '../game';
import type { Team } from '../types';
import { BOARD, sideWinChance } from './board';

/**
 * The AI's preset pick (slice 10c), a first step toward the roster-building AI: once the player
 * locks a roster, the AI fields whichever of its teams stands better against it on the matchup
 * board. No rng: the pick is a function of the two rosters (and the numbers).
 */

/**
 * How `ours` stands against `theirs` on the board, as each would start: the mean win chance over
 * the seven board battles with `ours` on offense and the seven with it on defense, each Pokémon
 * fresh (full HP, no stages), from the same numbers the AI's other rules read.
 */
export function boardScore(ours: Team, theirs: Team, b: Balance): number {
  const ourOffense = lineupOf(ours, ours.offense);
  const ourDefense = lineupOf(ours, ours.defense);
  const theirOffense = lineupOf(theirs, theirs.offense);
  const theirDefense = lineupOf(theirs, theirs.defense);
  let sum = 0;
  for (const l of BOARD) {
    sum += sideWinChance('offense', l.kind, ourOffense[l.offense], theirDefense[l.defense], b);
    sum += sideWinChance('defense', l.kind, ourDefense[l.defense], theirOffense[l.offense], b);
  }
  return sum / (2 * BOARD.length);
}

/**
 * The candidate the AI fields against `player`: the best board score, the first listed on a tie.
 */
export function pickOpponent<Id extends string>(
  player: Team,
  candidates: readonly { id: Id; team: Team }[],
  b: Balance = defaultBalance,
): { id: Id; score: number } {
  let best: { id: Id; score: number } | undefined;
  for (const c of candidates) {
    const score = boardScore(c.team, player, b);
    if (!best || score > best.score) best = { id: c.id, score };
  }
  if (!best) throw new Error('pickOpponent: no candidates');
  return best;
}
